import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Layout, Type, Image as ImageIcon, Video, Users, CreditCard, List, Quote, 
  MessageSquare, Clock, DollarSign, GripVertical, Trash2, ChevronUp, ChevronDown,
  Save, Eye, Smartphone, Monitor, Undo2, Redo2, X, Plus, Palette, Settings,
  Copy, Download, Upload, Sparkles, Check, Search, Filter, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Enhanced Component Types with more options
const COMPONENT_LIBRARY = {
  sections: {
    label: 'Sections',
    icon: Layout,
    components: [
      { id: 'hero', label: 'Hero Section', icon: Layout, description: 'Main banner with headline & CTA' },
      { id: 'features', label: 'Features Grid', icon: List, description: 'Showcase features in grid' },
      { id: 'pricing', label: 'Pricing Table', icon: DollarSign, description: 'Display pricing plans' },
      { id: 'faq', label: 'FAQ Section', icon: MessageSquare, description: 'Frequently asked questions' }
    ]
  },
  content: {
    label: 'Content',
    icon: Type,
    components: [
      { id: 'text', label: 'Text Block', icon: Type, description: 'Paragraph or formatted text' },
      { id: 'heading', label: 'Heading', icon: Type, description: 'Section headings' },
      { id: 'divider', label: 'Divider', icon: Layout, description: 'Visual separator' },
      { id: 'spacer', label: 'Spacer', icon: Layout, description: 'Add vertical space' }
    ]
  },
  media: {
    label: 'Media',
    icon: ImageIcon,
    components: [
      { id: 'image', label: 'Image', icon: ImageIcon, description: 'Single image display' },
      { id: 'video', label: 'Video Embed', icon: Video, description: 'YouTube/Vimeo embed' },
      { id: 'gallery', label: 'Image Gallery', icon: ImageIcon, description: 'Multiple images' }
    ]
  },
  conversion: {
    label: 'Conversion',
    icon: Users,
    components: [
      { id: 'form', label: 'Lead Form', icon: Users, description: 'Capture lead information' },
      { id: 'cta', label: 'Call to Action', icon: MessageSquare, description: 'Action button block' },
      { id: 'checkout', label: 'Checkout', icon: CreditCard, description: 'Payment checkout' },
      { id: 'countdown', label: 'Countdown', icon: Clock, description: 'Urgency timer' }
    ]
  },
  social_proof: {
    label: 'Social Proof',
    icon: Quote,
    components: [
      { id: 'testimonial', label: 'Testimonials', icon: Quote, description: 'Customer reviews' },
      { id: 'logos', label: 'Logo Cloud', icon: ImageIcon, description: 'Partner/client logos' },
      { id: 'stats', label: 'Stats Counter', icon: DollarSign, description: 'Number statistics' }
    ]
  }
};

// Pre-built Section Templates
const SECTION_TEMPLATES = [
  {
    id: 'hero-centered',
    name: 'Centered Hero',
    category: 'hero',
    preview: '🎯',
    component: {
      type: 'hero',
      props: {
        headline: 'Your Compelling Headline Here',
        subheadline: 'A brief description that explains your value proposition',
        buttonText: 'Get Started',
        buttonUrl: '#form',
        alignment: 'center'
      }
    }
  },
  {
    id: 'hero-split',
    name: 'Split Hero',
    category: 'hero',
    preview: '⚡',
    component: {
      type: 'hero',
      props: {
        headline: 'Transform Your Business Today',
        subheadline: 'Join thousands of satisfied customers',
        buttonText: 'Start Free Trial',
        layout: 'split',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600'
      }
    }
  },
  {
    id: 'features-3col',
    name: '3-Column Features',
    category: 'features',
    preview: '📊',
    component: {
      type: 'features',
      props: {
        title: 'Why Choose Us',
        columns: 3,
        features: [
          { icon: '⚡', title: 'Fast', description: 'Lightning quick performance' },
          { icon: '🔒', title: 'Secure', description: 'Enterprise-grade security' },
          { icon: '💪', title: 'Powerful', description: 'Feature-rich platform' }
        ]
      }
    }
  },
  {
    id: 'testimonial-carousel',
    name: 'Testimonial Slider',
    category: 'testimonial',
    preview: '💬',
    component: {
      type: 'testimonial',
      props: {
        title: 'What Our Customers Say',
        layout: 'carousel',
        testimonials: [
          { name: 'Happy Customer', role: 'CEO', text: 'Amazing product!', rating: 5 }
        ]
      }
    }
  },
  {
    id: 'form-simple',
    name: 'Simple Lead Form',
    category: 'form',
    preview: '📝',
    component: {
      type: 'form',
      props: {
        title: 'Get Started Today',
        fields: [
          { name: 'name', label: 'Name', type: 'text', required: true },
          { name: 'email', label: 'Email', type: 'email', required: true },
          { name: 'phone', label: 'Phone', type: 'tel', required: true }
        ],
        buttonText: 'Submit'
      }
    }
  },
  {
    id: 'pricing-3tier',
    name: '3-Tier Pricing',
    category: 'pricing',
    preview: '💰',
    component: {
      type: 'pricing',
      props: {
        title: 'Simple Pricing',
        plans: [
          { name: 'Basic', price: '₹999', features: ['Feature 1', 'Feature 2'] },
          { name: 'Pro', price: '₹2999', features: ['All Basic', 'Feature 3', 'Feature 4'], popular: true },
          { name: 'Enterprise', price: 'Custom', features: ['All Pro', 'Dedicated Support'] }
        ]
      }
    }
  }
];

export default function PageBuilder({ funnel, page, token, onClose, onSave }) {
  // State
  const [components, setComponents] = useState(page?.components || []);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop, mobile
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showSectionLibrary, setShowSectionLibrary] = useState(false);
  const [showTemplateMarketplace, setShowTemplateMarketplace] = useState(false);
  const [theme, setTheme] = useState(funnel?.theme || {
    primary_color: '#059669',
    secondary_color: '#10B981',
    background_color: '#0f172a',
    text_color: '#ffffff',
    font_family: 'Inter'
  });
  const [draggedItem, setDraggedItem] = useState(null);
  const [activeCategory, setActiveCategory] = useState('sections');
  
  const canvasRef = useRef(null);

  // Initialize history
  useEffect(() => {
    if (components.length > 0 && history.length === 0) {
      setHistory([JSON.stringify(components)]);
      setHistoryIndex(0);
    }
  }, []);

  // Add to history
  const addToHistory = useCallback((newComponents) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newComponents));
    if (newHistory.length > 50) newHistory.shift(); // Limit history
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setComponents(JSON.parse(history[newIndex]));
      toast.info('Undone');
    }
  }, [history, historyIndex]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setComponents(JSON.parse(history[newIndex]));
      toast.info('Redone');
    }
  }, [history, historyIndex]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveComponents();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // Add component
  const addComponent = (componentType, index = null, props = {}) => {
    const newComponent = {
      id: `${componentType}-${Date.now()}`,
      type: componentType,
      props: props
    };

    const newComponents = [...components];
    if (index !== null) {
      newComponents.splice(index, 0, newComponent);
    } else {
      newComponents.push(newComponent);
    }

    setComponents(newComponents);
    addToHistory(newComponents);
    setSelectedComponent(newComponent);
  };

  // Add section template
  const addSectionTemplate = (template) => {
    const newComponent = {
      id: `${template.component.type}-${Date.now()}`,
      ...template.component
    };
    const newComponents = [...components, newComponent];
    setComponents(newComponents);
    addToHistory(newComponents);
    setSelectedComponent(newComponent);
    setShowSectionLibrary(false);
    toast.success(`Added ${template.name}`);
  };

  // Update component
  const updateComponent = (componentId, updates) => {
    const newComponents = components.map(c => 
      c.id === componentId ? { ...c, ...updates } : c
    );
    setComponents(newComponents);
    addToHistory(newComponents);
    if (selectedComponent?.id === componentId) {
      setSelectedComponent({ ...selectedComponent, ...updates });
    }
  };

  // Delete component
  const deleteComponent = (componentId) => {
    const newComponents = components.filter(c => c.id !== componentId);
    setComponents(newComponents);
    addToHistory(newComponents);
    if (selectedComponent?.id === componentId) {
      setSelectedComponent(null);
    }
  };

  // Move component
  const moveComponent = (fromIndex, toIndex) => {
    const newComponents = [...components];
    const [moved] = newComponents.splice(fromIndex, 1);
    newComponents.splice(toIndex, 0, moved);
    setComponents(newComponents);
    addToHistory(newComponents);
  };

  // Duplicate component
  const duplicateComponent = (componentId) => {
    const component = components.find(c => c.id === componentId);
    if (component) {
      const newComponent = {
        ...JSON.parse(JSON.stringify(component)),
        id: `${component.type}-${Date.now()}`
      };
      const index = components.findIndex(c => c.id === componentId);
      const newComponents = [...components];
      newComponents.splice(index + 1, 0, newComponent);
      setComponents(newComponents);
      addToHistory(newComponents);
      toast.success('Component duplicated');
    }
  };

  // Drag handlers
  const handleDragStart = (e, componentType, fromIndex = null) => {
    setDraggedItem({ type: componentType, fromIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    
    if (!draggedItem) return;

    if (draggedItem.fromIndex !== null) {
      // Reordering existing component
      if (draggedItem.fromIndex !== toIndex) {
        moveComponent(draggedItem.fromIndex, toIndex);
      }
    } else {
      // Adding new component
      addComponent(draggedItem.type, toIndex);
    }

    setDraggedItem(null);
  };

  // Save
  const saveComponents = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/funnels/${funnel.id}/pages/${page.id}/components`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(components)
      });

      if (res.ok) {
        toast.success('Page saved!');
        onSave?.();
      } else {
        toast.error('Failed to save page');
      }
    } catch (error) {
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-950 z-[60] flex flex-col"
    >
      {/* Top Bar */}
      <div className="h-14 border-b border-emerald-800/30 flex items-center justify-between px-4 bg-[#052e22]/80 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onClose} className="text-emerald-300/70 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div className="h-6 w-px bg-emerald-800/30" />
          <div>
            <h2 className="font-semibold text-white">{page?.name || 'Page Editor'}</h2>
            <p className="text-xs text-emerald-300/70">/{funnel?.slug}/{page?.slug}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <div className="flex items-center gap-1 mr-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={undo}
              disabled={historyIndex <= 0}
              className="text-emerald-300/70 hover:text-white"
              title="Undo (Ctrl+Z)"
            >
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
              className="text-emerald-300/70 hover:text-white"
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo2 className="w-4 h-4" />
            </Button>
          </div>

          <div className="h-6 w-px bg-emerald-800/30" />

          {/* Preview Mode Toggle */}
          <div className="flex items-center gap-1 bg-[#0a3d2e] rounded-lg p-1">
            <button
              onClick={() => setPreviewMode('desktop')}
              className={`p-2 rounded ${previewMode === 'desktop' ? 'bg-emerald-600 text-white' : 'text-emerald-300/70 hover:text-white'}`}
              title="Desktop view"
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewMode('mobile')}
              className={`p-2 rounded ${previewMode === 'mobile' ? 'bg-emerald-600 text-white' : 'text-emerald-300/70 hover:text-white'}`}
              title="Mobile view"
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>

          <div className="h-6 w-px bg-emerald-800/30" />

          {/* Preview Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="border-emerald-800/30 text-emerald-300/70 hover:text-white"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>

          {/* Save Button */}
          <Button
            onClick={saveComponents}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Component Palette */}
        <div className="w-72 border-r border-emerald-800/30 bg-[#052e22]/50 flex flex-col overflow-hidden">
          {/* Section Library Button */}
          <div className="p-3 border-b border-emerald-800/30">
            <Button
              onClick={() => setShowSectionLibrary(true)}
              className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Section Library
            </Button>
          </div>

          {/* Component Categories */}
          <div className="flex border-b border-emerald-800/30 overflow-x-auto">
            {Object.entries(COMPONENT_LIBRARY).map(([key, category]) => {
              const Icon = category.icon;
              return (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className={`flex-1 min-w-0 py-2 px-3 text-xs font-medium transition-colors ${
                    activeCategory === key
                      ? 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-500/10'
                      : 'text-emerald-300/70 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4 mx-auto mb-1" />
                  <span className="block truncate">{category.label}</span>
                </button>
              );
            })}
          </div>

          {/* Components List */}
          <div className="flex-1 overflow-y-auto p-3">
            <div className="space-y-2">
              {COMPONENT_LIBRARY[activeCategory]?.components.map(comp => {
                const Icon = comp.icon;
                return (
                  <div
                    key={comp.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, comp.id)}
                    className="flex items-start gap-3 p-3 rounded-lg bg-[#0a3d2e]/50 hover:bg-emerald-500/20 border border-emerald-800/30 hover:border-emerald-500/50 cursor-grab active:cursor-grabbing transition-all group"
                  >
                    <div className="p-2 rounded-lg bg-emerald-500/20 group-hover:bg-emerald-500/30">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{comp.label}</p>
                      <p className="text-xs text-emerald-300/50 truncate">{comp.description}</p>
                    </div>
                    <GripVertical className="w-4 h-4 text-emerald-800/50 group-hover:text-emerald-400" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div 
          ref={canvasRef}
          className="flex-1 overflow-y-auto bg-slate-900 p-8"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, components.length)}
        >
          <div 
            className={`mx-auto transition-all duration-300 ${
              previewMode === 'mobile' ? 'max-w-[375px]' : 'max-w-4xl'
            }`}
            style={{ backgroundColor: theme.background_color }}
          >
            {components.length === 0 ? (
              <div 
                className="border-2 border-dashed border-emerald-800/30 rounded-xl p-12 text-center"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 0)}
              >
                <Layout className="w-16 h-16 text-emerald-800/50 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Start Building Your Page</h3>
                <p className="text-emerald-300/70 mb-4">Drag components from the left panel or use the Section Library</p>
                <Button
                  onClick={() => setShowSectionLibrary(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Browse Section Library
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {components.map((component, index) => (
                  <CanvasComponent
                    key={component.id}
                    component={component}
                    index={index}
                    totalCount={components.length}
                    isSelected={selectedComponent?.id === component.id}
                    onSelect={() => setSelectedComponent(component)}
                    onDelete={() => deleteComponent(component.id)}
                    onDuplicate={() => duplicateComponent(component.id)}
                    onMoveUp={() => index > 0 && moveComponent(index, index - 1)}
                    onMoveDown={() => index < components.length - 1 && moveComponent(index, index + 1)}
                    onDragStart={(e) => handleDragStart(e, component.type, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    theme={theme}
                    previewMode={previewMode}
                  />
                ))}
                <div
                  className="border-2 border-dashed border-emerald-800/30 rounded-lg p-4 text-center text-emerald-300/50 text-sm hover:border-emerald-500/50 hover:text-emerald-300/70 transition-colors"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, components.length)}
                >
                  Drop component here
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Properties Panel */}
        {selectedComponent && (
          <div className="w-80 border-l border-emerald-800/30 bg-[#052e22]/50 flex flex-col overflow-hidden">
            <div className="p-4 border-b border-emerald-800/30 flex items-center justify-between">
              <h3 className="font-semibold text-white">Component Settings</h3>
              <Button
                size="sm"
                variant="ghost"
                className="text-emerald-300/70 hover:text-white"
                onClick={() => setSelectedComponent(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <ComponentPropertiesEditor
                component={selectedComponent}
                onUpdate={(updates) => updateComponent(selectedComponent.id, updates)}
                theme={theme}
              />
            </div>
          </div>
        )}
      </div>

      {/* Section Library Modal */}
      <AnimatePresence>
        {showSectionLibrary && (
          <SectionLibraryModal
            onClose={() => setShowSectionLibrary(false)}
            onSelect={addSectionTemplate}
          />
        )}
      </AnimatePresence>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && (
          <PreviewModal
            components={components}
            theme={theme}
            previewMode={previewMode}
            onClose={() => setShowPreview(false)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Canvas Component (individual component on canvas)
function CanvasComponent({ 
  component, index, totalCount, isSelected, 
  onSelect, onDelete, onDuplicate, onMoveUp, onMoveDown,
  onDragStart, onDragOver, onDrop, theme, previewMode 
}) {
  const componentInfo = Object.values(COMPONENT_LIBRARY)
    .flatMap(cat => cat.components)
    .find(c => c.id === component.type);
  const Icon = componentInfo?.icon || Layout;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={onSelect}
      className={`relative group rounded-lg border-2 transition-all cursor-pointer ${
        isSelected 
          ? 'border-emerald-500 ring-2 ring-emerald-500/20' 
          : 'border-transparent hover:border-emerald-800/50'
      }`}
    >
      {/* Component Label */}
      <div className={`absolute -top-3 left-4 z-10 flex items-center gap-2 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <Badge className="bg-emerald-600 text-white text-xs py-0.5 flex items-center gap-1">
          <Icon className="w-3 h-3" />
          {componentInfo?.label || component.type}
        </Badge>
      </div>

      {/* Component Controls */}
      <div className={`absolute -top-3 right-4 z-10 flex items-center gap-1 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }} className="p-1 bg-slate-700 rounded hover:bg-slate-600" title="Duplicate">
          <Copy className="w-3 h-3 text-white" />
        </button>
        {index > 0 && (
          <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} className="p-1 bg-slate-700 rounded hover:bg-slate-600" title="Move Up">
            <ChevronUp className="w-3 h-3 text-white" />
          </button>
        )}
        {index < totalCount - 1 && (
          <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} className="p-1 bg-slate-700 rounded hover:bg-slate-600" title="Move Down">
            <ChevronDown className="w-3 h-3 text-white" />
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1 bg-red-500/80 rounded hover:bg-red-500" title="Delete">
          <Trash2 className="w-3 h-3 text-white" />
        </button>
      </div>

      {/* Component Preview */}
      <div className="p-6">
        <ComponentPreview component={component} theme={theme} previewMode={previewMode} />
      </div>
    </div>
  );
}

// Component Preview Renderer
function ComponentPreview({ component, theme, previewMode }) {
  const props = component.props || {};
  const isMobile = previewMode === 'mobile';

  switch (component.type) {
    case 'hero':
      return (
        <div 
          className={`py-16 px-8 text-center rounded-lg ${isMobile ? 'py-8 px-4' : ''}`}
          style={{ 
            backgroundImage: props.backgroundImage ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${props.backgroundImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {props.badge && (
            <span className="inline-block px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm mb-4">
              {props.badge}
            </span>
          )}
          <h1 className={`font-bold text-white mb-4 ${isMobile ? 'text-2xl' : 'text-4xl'}`}>
            {props.headline || 'Your Headline Here'}
          </h1>
          <p className={`text-emerald-300/70 mb-6 max-w-2xl mx-auto ${isMobile ? 'text-sm' : 'text-lg'}`}>
            {props.subheadline || 'Your subheadline goes here'}
          </p>
          <button 
            className={`px-8 py-3 rounded-lg font-semibold text-white ${isMobile ? 'px-6 py-2 text-sm' : ''}`}
            style={{ backgroundColor: theme.primary_color }}
          >
            {props.buttonText || 'Get Started'}
          </button>
        </div>
      );

    case 'features':
      const features = props.features || [
        { icon: '⚡', title: 'Feature 1', description: 'Description here' },
        { icon: '🔒', title: 'Feature 2', description: 'Description here' },
        { icon: '💪', title: 'Feature 3', description: 'Description here' }
      ];
      return (
        <div className="py-12 px-8">
          <h2 className={`font-bold text-white text-center mb-8 ${isMobile ? 'text-xl' : 'text-2xl'}`}>
            {props.title || 'Features'}
          </h2>
          <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
            {features.map((feature, i) => (
              <div key={i} className="text-center p-4">
                <div className="text-3xl mb-3">{feature.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-emerald-300/70 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case 'form':
      const fields = props.fields || [
        { name: 'name', label: 'Name', type: 'text' },
        { name: 'email', label: 'Email', type: 'email' }
      ];
      return (
        <div className="py-12 px-8 max-w-md mx-auto">
          <h2 className="text-xl font-bold text-white text-center mb-2">
            {props.title || 'Contact Us'}
          </h2>
          {props.subtitle && (
            <p className="text-emerald-300/70 text-center mb-6 text-sm">{props.subtitle}</p>
          )}
          <div className="space-y-4">
            {fields.map((field, i) => (
              <div key={i}>
                <label className="block text-sm text-emerald-300/70 mb-1">{field.label}</label>
                <input
                  type={field.type}
                  placeholder={field.label}
                  className="w-full px-4 py-2 bg-slate-800 border border-emerald-800/30 rounded-lg text-white"
                  disabled
                />
              </div>
            ))}
            <button 
              className="w-full py-3 rounded-lg font-semibold text-white"
              style={{ backgroundColor: theme.primary_color }}
            >
              {props.buttonText || 'Submit'}
            </button>
          </div>
        </div>
      );

    case 'testimonial':
      const testimonials = props.testimonials || [
        { name: 'Happy Customer', role: 'CEO', text: 'Great product!', rating: 5 }
      ];
      return (
        <div className="py-12 px-8">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            {props.title || 'Testimonials'}
          </h2>
          <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {testimonials.map((t, i) => (
              <div key={i} className="bg-slate-800/50 p-6 rounded-lg">
                <div className="flex text-yellow-400 mb-3">
                  {[...Array(t.rating || 5)].map((_, j) => (
                    <span key={j}>⭐</span>
                  ))}
                </div>
                <p className="text-emerald-300/70 mb-4 italic">"{t.text}"</p>
                <div>
                  <p className="font-semibold text-white">{t.name}</p>
                  <p className="text-sm text-emerald-300/50">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'pricing':
      return (
        <div className="py-12 px-8">
          <h2 className="text-2xl font-bold text-white text-center mb-4">
            {props.title || 'Pricing'}
          </h2>
          <div className="text-center">
            <span className="text-4xl font-bold text-white">{props.price || '₹999'}</span>
            {props.originalPrice && (
              <span className="ml-2 text-lg text-emerald-300/50 line-through">{props.originalPrice}</span>
            )}
          </div>
          {props.discount && (
            <p className="text-center text-emerald-400 mt-2">{props.discount}</p>
          )}
        </div>
      );

    case 'countdown':
      return (
        <div className="py-8 px-8 text-center">
          <h3 className="text-lg font-semibold text-white mb-4">{props.title || 'Offer Ends In'}</h3>
          <div className="flex justify-center gap-4">
            {['Days', 'Hours', 'Mins', 'Secs'].map((unit, i) => (
              <div key={i} className="bg-slate-800 px-4 py-3 rounded-lg">
                <div className="text-2xl font-bold text-white">00</div>
                <div className="text-xs text-emerald-300/50">{unit}</div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'cta':
      return (
        <div className="py-12 px-8 text-center" style={{ backgroundColor: theme.primary_color + '20' }}>
          <h2 className="text-2xl font-bold text-white mb-4">{props.title || 'Ready to Get Started?'}</h2>
          <p className="text-emerald-300/70 mb-6">{props.subtitle || 'Join thousands of happy customers'}</p>
          <button 
            className="px-8 py-3 rounded-lg font-semibold text-white"
            style={{ backgroundColor: theme.primary_color }}
          >
            {props.buttonText || 'Get Started'}
          </button>
        </div>
      );

    case 'text':
      return (
        <div className="py-6 px-8">
          <p className="text-emerald-300/70">{props.content || 'Your text content goes here...'}</p>
        </div>
      );

    case 'image':
      return (
        <div className="py-6 px-8">
          <img 
            src={props.src || 'https://via.placeholder.com/800x400'} 
            alt={props.alt || 'Image'}
            className="w-full rounded-lg"
          />
        </div>
      );

    default:
      return (
        <div className="py-6 px-8 text-center text-emerald-300/50">
          <Layout className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>{component.type} component</p>
        </div>
      );
  }
}

// Component Properties Editor
function ComponentPropertiesEditor({ component, onUpdate, theme }) {
  const props = component.props || {};

  const updateProp = (key, value) => {
    onUpdate({ props: { ...props, [key]: value } });
  };

  return (
    <div className="space-y-4">
      {/* Common Props based on component type */}
      {component.type === 'hero' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Headline</label>
            <input
              type="text"
              value={props.headline || ''}
              onChange={(e) => updateProp('headline', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="Your headline"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Subheadline</label>
            <textarea
              value={props.subheadline || ''}
              onChange={(e) => updateProp('subheadline', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm resize-none"
              placeholder="Supporting text"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Button Text</label>
            <input
              type="text"
              value={props.buttonText || ''}
              onChange={(e) => updateProp('buttonText', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="Button text"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Badge Text</label>
            <input
              type="text"
              value={props.badge || ''}
              onChange={(e) => updateProp('badge', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="e.g., ⭐ New Feature"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Background Image URL</label>
            <input
              type="text"
              value={props.backgroundImage || ''}
              onChange={(e) => updateProp('backgroundImage', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="https://..."
            />
          </div>
        </>
      )}

      {component.type === 'form' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Form Title</label>
            <input
              type="text"
              value={props.title || ''}
              onChange={(e) => updateProp('title', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Subtitle</label>
            <input
              type="text"
              value={props.subtitle || ''}
              onChange={(e) => updateProp('subtitle', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Button Text</label>
            <input
              type="text"
              value={props.buttonText || ''}
              onChange={(e) => updateProp('buttonText', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {component.type === 'features' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Section Title</label>
            <input
              type="text"
              value={props.title || ''}
              onChange={(e) => updateProp('title', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {component.type === 'testimonial' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Section Title</label>
            <input
              type="text"
              value={props.title || ''}
              onChange={(e) => updateProp('title', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {component.type === 'cta' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Title</label>
            <input
              type="text"
              value={props.title || ''}
              onChange={(e) => updateProp('title', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Subtitle</label>
            <input
              type="text"
              value={props.subtitle || ''}
              onChange={(e) => updateProp('subtitle', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Button Text</label>
            <input
              type="text"
              value={props.buttonText || ''}
              onChange={(e) => updateProp('buttonText', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {component.type === 'text' && (
        <div>
          <label className="block text-sm text-emerald-300/70 mb-2">Content</label>
          <textarea
            value={props.content || ''}
            onChange={(e) => updateProp('content', e.target.value)}
            rows={6}
            className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm resize-none"
          />
        </div>
      )}

      {component.type === 'countdown' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Title</label>
            <input
              type="text"
              value={props.title || ''}
              onChange={(e) => updateProp('title', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">End Date</label>
            <input
              type="datetime-local"
              value={props.endDate?.slice(0, 16) || ''}
              onChange={(e) => updateProp('endDate', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {component.type === 'pricing' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Title</label>
            <input
              type="text"
              value={props.title || ''}
              onChange={(e) => updateProp('title', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Price</label>
            <input
              type="text"
              value={props.price || ''}
              onChange={(e) => updateProp('price', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="₹999"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Original Price (optional)</label>
            <input
              type="text"
              value={props.originalPrice || ''}
              onChange={(e) => updateProp('originalPrice', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="₹1999"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Discount Text</label>
            <input
              type="text"
              value={props.discount || ''}
              onChange={(e) => updateProp('discount', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="50% OFF"
            />
          </div>
        </>
      )}
    </div>
  );
}

// Section Library Modal
function SectionLibraryModal({ onClose, onSelect }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', ...new Set(SECTION_TEMPLATES.map(t => t.category))];
  
  const filteredTemplates = SECTION_TEMPLATES.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#052e22] border border-emerald-800/30 rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-emerald-800/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              Section Library
            </h3>
            <Button variant="ghost" onClick={onClose} className="text-emerald-300/70">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Search & Filter */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-300/50" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search sections..."
                className="w-full pl-10 pr-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-emerald-300/50"
              />
            </div>
            <div className="flex gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium capitalize ${
                    selectedCategory === cat
                      ? 'bg-emerald-600 text-white'
                      : 'bg-[#0a3d2e] text-emerald-300/70 hover:text-white'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-3 gap-4">
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                onClick={() => onSelect(template)}
                className="p-4 bg-[#0a3d2e]/50 border border-emerald-800/30 rounded-lg hover:border-emerald-500/50 cursor-pointer transition-all group"
              >
                <div className="text-4xl mb-3 text-center">{template.preview}</div>
                <h4 className="font-medium text-white text-center group-hover:text-emerald-400">
                  {template.name}
                </h4>
                <p className="text-xs text-emerald-300/50 text-center mt-1 capitalize">
                  {template.category}
                </p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Preview Modal
function PreviewModal({ components, theme, previewMode, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-8"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className={`bg-white rounded-xl overflow-hidden shadow-2xl ${
          previewMode === 'mobile' ? 'w-[375px]' : 'w-full max-w-5xl'
        } max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: theme.background_color }}
      >
        <div className="p-4 bg-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <p className="text-sm text-slate-400">Preview Mode</p>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-white">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div>
          {components.map((component, index) => (
            <ComponentPreview 
              key={component.id || index} 
              component={component} 
              theme={theme}
              previewMode={previewMode}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
